import { useState, useCallback, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import {
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Pill,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
} from "lucide-react";

export interface SummaryPreviewCardProps {
  drugName: { cn: string; en: string; inn: string };
  dosageForm: string;
  pkParams: {
    t12: string;
    bioavailability: string;
    cmax?: string;
    auc?: string;
    tmax?: string;
    vd?: string;
    cl?: string;
    proteinBinding?: string;
    notes?: string;
  };
  sourceCoverage: { covered: number; total: number };
  qualityScore: number;
  isCombination?: boolean;
  components?: Array<{ cn: string; en: string }>;
  onDownload?: () => Promise<void>;
  onViewDetail?: () => void;
}

type DownloadStatus = "idle" | "loading" | "success" | "error";

function QualityScoreBadge({ score }: { score: number }) {
  const colorClass =
    score >= 85
      ? "bg-success/15 text-success border-success/30"
      : score >= 70
        ? "bg-warning/15 text-warning border-warning/30"
        : "bg-destructive/15 text-destructive border-destructive/30";

  const label =
    score >= 85 ? "优秀" : score >= 70 ? "良好" : "需改进";

  return (
    <div className={cn("inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-semibold", colorClass)}>
      <span>{score}/100</span>
      <span className="text-xs font-normal opacity-70">{label}</span>
    </div>
  );
}

function SourceProgressBar({ covered, total }: { covered: number; total: number }) {
  const percentage = Math.round((covered / total) * 100);
  const filledBlocks = Math.round((covered / total) * 8);
  const emptyBlocks = 8 - filledBlocks;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">数据源覆盖</span>
        <span className="font-medium text-foreground">{covered}/{total}</span>
      </div>
      <div className="flex items-center gap-1">
        {Array.from({ length: filledBlocks }).map((_, i) => (
          <div key={`filled-${i}`} className="w-3 h-3 rounded-sm bg-primary" />
        ))}
        {Array.from({ length: emptyBlocks }).map((_, i) => (
          <div key={`empty-${i}`} className="w-3 h-3 rounded-sm bg-muted" />
        ))}
        <span className="ml-2 text-xs text-muted-foreground">{percentage}%</span>
      </div>
    </div>
  );
}

function CombinationComponents({
  components,
}: {
  components: Array<{ cn: string; en: string }>;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-warning/30 bg-warning/5 overflow-hidden transition-all duration-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-warning/10 transition-colors"
      >
        <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0" />
        <span className="font-medium text-warning">
          复方制剂 ({components.length}种活性成分)
        </span>
        {expanded ? (
          <ChevronDown className={cn("w-4 h-4 text-warning ml-auto transition-transform duration-200")} />
        ) : (
          <ChevronRight className={cn("w-4 h-4 text-warning ml-auto transition-transform duration-200")} />
        )}
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          expanded ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="px-3 pb-2 space-y-1">
          {components.map((comp, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 text-sm py-1 pl-6 animate-slideDown"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <span className="text-muted-foreground text-xs select-none">
                {idx === components.length - 1 ? "└─" : "├─"}
              </span>
              <span className="text-muted-foreground">组分{idx + 1}:</span>
              <span className="font-medium text-foreground">{comp.cn}</span>
              <span className="text-muted-foreground">({comp.en})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DownloadButton({
  status,
  onClick,
}: {
  status: DownloadStatus;
  onClick: () => void;
}) {
  if (status === "loading") {
    return (
      <button disabled className="btn btn-primary opacity-80 cursor-wait gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>生成中...</span>
        <span className="inline-block w-16 h-1.5 bg-white/20 rounded-full overflow-hidden">
          <span className="block h-full w-2/3 bg-white/60 animate-pulse rounded-full" style={{ animationDuration: "1s" }} />
        </span>
      </button>
    );
  }
  if (status === "success") {
    return (
      <button className="btn bg-success text-white hover:bg-success/90 border-0 gap-2">
        <CheckCircle2 className="w-4 h-4" />
        下载完成
      </button>
    );
  }
  if (status === "error") {
    return (
      <button onClick={onClick} className="btn btn-danger gap-2">
        <XCircle className="w-4 h-4" />
        重试下载
      </button>
    );
  }
  return (
    <button onClick={onClick} className="btn btn-primary gap-2">
      <Download className="w-4 h-4" />
      下载完整报告
    </button>
  );
}

interface PKParamItem {
  key: string;
  notation: React.ReactNode;
  label: string;
  value: string | undefined;
  fallbackUnit?: string;
}

function normalizePkTableValue(value: string | undefined, fallbackUnit?: string) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text || text === "—" || text === "-") return "";
  if (text.length > 160) return "";
  if (
    /(pharmacokinetics|clinical pharmacology|after a single|following oral|section\s*\d+|^\d+\.\d+\s+[A-Z][a-z]+)/i.test(text) &&
    text.length > 35
  ) {
    return "";
  }
  if (!fallbackUnit || /[%a-zA-Zμµ]/.test(text)) return text;
  return `${text} ${fallbackUnit}`;
}

function PkNotation({ type }: { type: string }) {
  switch (type) {
    case "cmax":
      return <><i>C</i><sub>max</sub></>;
    case "auc":
      return <i>AUC</i>;
    case "tmax":
      return <><i>T</i><sub>max</sub></>;
    case "t12":
      return <><i>t</i><sub>1/2</sub></>;
    case "bioavailability":
      return <i>F</i>;
    case "vd":
      return <><i>V</i><sub>d</sub></>;
    case "cl":
      return <i>CL</i>;
    case "proteinBinding":
      return <i>PPB</i>;
    default:
      return <i>{type}</i>;
  }
}

function PKParamsGrid({ pkParams }: { pkParams: SummaryPreviewCardProps["pkParams"] }) {
  const [showNotes, setShowNotes] = useState(false);

  const params: PKParamItem[] = [
    {
      key: "cmax",
      notation: <PkNotation type="cmax" />,
      label: "峰浓度",
      value: pkParams.cmax,
    },
    {
      key: "auc",
      notation: <PkNotation type="auc" />,
      label: "药时曲线下面积",
      value: pkParams.auc,
    },
    {
      key: "tmax",
      notation: <PkNotation type="tmax" />,
      label: "达峰时间",
      value: pkParams.tmax,
      fallbackUnit: "h",
    },
    {
      key: "t12",
      notation: <PkNotation type="t12" />,
      label: "消除半衰期",
      value: pkParams.t12,
      fallbackUnit: "h",
    },
    {
      key: "bioavailability",
      notation: <PkNotation type="bioavailability" />,
      label: "生物利用度",
      value: pkParams.bioavailability,
      fallbackUnit: "%",
    },
    {
      key: "vd",
      notation: <PkNotation type="vd" />,
      label: "表观分布容积",
      value: pkParams.vd,
    },
    {
      key: "cl",
      notation: <PkNotation type="cl" />,
      label: "清除率",
      value: pkParams.cl,
    },
    {
      key: "proteinBinding",
      notation: <PkNotation type="proteinBinding" />,
      label: "血浆蛋白结合率",
      value: pkParams.proteinBinding,
      fallbackUnit: "%",
    },
  ];

  const visibleParams = params
    .map((param) => ({
      ...param,
      value: normalizePkTableValue(param.value, param.fallbackUnit),
    }))
    .filter((param) => param.value);
  const hasNotes = pkParams.notes && pkParams.notes.trim().length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Pill className="w-4 h-4 text-primary" />
          药代动力学参数 (PK)
        </h4>
        <span className="text-xs text-muted-foreground">
          {visibleParams.length} 项参数
        </span>
      </div>

      {visibleParams.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-border">
          <table aria-label="药代动力学参数表" className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <th className="w-28 px-3 py-2.5 text-left font-medium">参数</th>
                <th className="px-3 py-2.5 text-left font-medium">统计含义</th>
                <th className="min-w-[12rem] px-3 py-2.5 text-left font-medium">数值 / 单位</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
              {visibleParams.map((param) => (
                <tr key={param.key} className="hover:bg-muted/20">
                  <td className="px-3 py-2.5 text-base font-semibold text-foreground">
                    {param.notation}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {param.label}
                  </td>
                  <td className="px-3 py-2.5 font-medium tabular-nums text-foreground">
                    {param.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-3 text-sm text-muted-foreground">
          暂无可表格化的关键 PK 参数；完整原文可在下方 FDA 数据源中查看。
        </div>
      )}

      {hasNotes && (
        <div className="rounded-lg border border-border bg-muted/10 p-3">
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-2 w-full text-left"
          >
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              来源说明
            </span>
            {showNotes ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
            )}
          </button>
          {showNotes && (
            <p className="mt-2 text-sm text-foreground leading-relaxed pl-6">
              {pkParams.notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function SummaryPreviewCard({
  drugName,
  dosageForm,
  pkParams,
  sourceCoverage,
  qualityScore,
  isCombination = false,
  components = [],
  onDownload,
  onViewDetail,
}: SummaryPreviewCardProps) {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>("idle");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const current = pollRef.current;
    return () => {
      if (current) clearInterval(current);
    };
  }, []);

  const handleDownload = useCallback(async () => {
    if (!onDownload || downloadStatus === "loading") return;
    setDownloadStatus("loading");
    try {
      await onDownload();
      setDownloadStatus("success");
    } catch {
      setDownloadStatus("error");
    }
  }, [onDownload, downloadStatus]);

  return (
    <div className="card animate-fadeIn overflow-hidden">
      <div className="p-5 sm:p-6 space-y-5">
        {/* Header: Drug Name + Icon */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Pill className="w-6 h-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-foreground leading-tight break-words">
                {drugName.cn}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground break-all">
                  {drugName.inn || drugName.en}
                </span>
                <span className="badge badge-info">{dosageForm}</span>
              </div>
            </div>
          </div>
          <div className="self-start flex-shrink-0">
            <QualityScoreBadge score={qualityScore} />
          </div>
        </div>

        {/* Combination Drug Warning */}
        {isCombination && components.length > 0 && (
          <CombinationComponents components={components} />
        )}

        {/* PK Parameters - Structured Display */}
        <PKParamsGrid pkParams={pkParams} />

        {/* Source Coverage Progress */}
        <SourceProgressBar
          covered={sourceCoverage.covered}
          total={sourceCoverage.total}
        />

        {/* Divider */}
        <div className="divider" />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <DownloadButton status={downloadStatus} onClick={handleDownload} />
          {onViewDetail && (
            <button onClick={onViewDetail} className="btn btn-secondary gap-2">
              <Search className="w-4 h-4" />
              在线查看详情
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
